import React, { useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { api } from "@/redux/api";
import message from "@/utils/globalMessage";
import { formatContractRef } from "@/utils/entity-ref";
import { useAuthEditable } from "@/hooks/use-auth";
import { buildFacilityPath, buildPortfolioPath } from "@/utils/route-context";
import appActions from "@/redux/app/actions";
import companyActions from "@/redux/company/actions";
import facilityActions from "@/redux/facility/actions";
import {
  WORKORDER_CREATE_PREFILL_KEY,
  writeFlowPrefill,
} from "@/utils/flow-prefill";
import ContractDetail from "@/containers/contract/ContractDetail";

const toAmountNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
};

const buildFacilitySelection = (facility) => ({
  Id: facility.id,
  key: facility.id,
  value: facility.id,
  label: facility.name,
  record: facility,
});

export default function PortfolioContractDetailPage() {
  const { companyId, id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userFacilities = useSelector((state) => state.Auth?.user_facilities);
  // contract:change gates both note writing and editing the CC email list.
  const canEditContract = useAuthEditable("contract");
  const canAddNote = canEditContract;
  const baseListPath = buildPortfolioPath("contract-request", { companyId }) || "/switch-view";
  const listPath = `${baseListPath}${location.search || ""}`;

  const loadRequest = useCallback(
    (requestId) => api.getContract(requestId),
    []
  );
  const addNote = useCallback(
    (requestId, body) => api.addContractNote(requestId, body),
    []
  );
  const deleteNote = useCallback(
    (requestId, noteId) => api.deleteContractNote(requestId, noteId),
    []
  );
  const deleteNoteAttachment = useCallback(
    (requestId, noteId, attachmentId) =>
      api.deleteContractNoteAttachment(requestId, noteId, attachmentId),
    []
  );
  const switchToFacilityContext = useCallback(
    (facilityId, fallbackCompanyId = companyId) => {
      const accessibleFacilities = Array.isArray(userFacilities) ? userFacilities : [];
      const facility = accessibleFacilities.find(
        (item) =>
          String(item.id) === String(facilityId) &&
          String(item.company_id) === String(fallbackCompanyId)
      );
      if (!facility) return null;
      dispatch(companyActions.setSelectedCompanyId(Number(facility.company_id)));
      dispatch(facilityActions.SelectedFacility(buildFacilitySelection(facility)));
      dispatch(appActions.setViewMode(0));
      return facility;
    },
    [companyId, dispatch, userFacilities]
  );
  const createWorkorder = useCallback(
    (record) => {
      const facility = switchToFacilityContext(record?.facility, record?.company_id || companyId);
      if (!facility) {
        message.error("The contract facility is not available in your account.");
        return;
      }
      const createPath = buildFacilityPath("create-workorder", {
        companyId: facility.company_id,
        facilityId: facility.id,
      });
      if (!createPath) return;
      const serviceRequestId = record.service_request_id || null;
      const saved = writeFlowPrefill(WORKORDER_CREATE_PREFILL_KEY, {
        source: "contract-request",
        source_id: record.id,
        sourceLabel: `Contract Request ${formatContractRef(record.id)}`,
        contract_request: record.id,
        service_request: serviceRequestId,
        service_request_id: serviceRequestId,
        facility: facility.id,
        equipments: record.equipments || [],
        service_category: record.service_category || null,
        issue: record.title || "",
        description: record.description || "",
        priority: record.priority || null,
        due_date: record.due_date || null,
        estimate_budget: toAmountNumber(record.awarded_amount),
        contractor: record.assignTo || null,
      });
      if (!saved) {
        message.error("Unable to prepare the work order form. Please try again.");
        return;
      }
      navigate(createPath);
    },
    [companyId, navigate, switchToFacilityContext]
  );
  const getWorkorderPath = useCallback(
    (record) => {
      const facility = switchToFacilityContext(
        record?.workorder_facility_id || record?.facility,
        record?.company_id || companyId
      );
      if (!facility || !record?.workorder_id) return "/switch-view";
      return (
        buildFacilityPath(`workorder-detail/${record.workorder_id}`, {
        companyId: facility.company_id,
        facilityId: facility.id,
      }) || "/switch-view"
      );
    },
    [companyId, switchToFacilityContext]
  );
  const getTenantRequestPath = useCallback(
    (record) => {
      const facility = switchToFacilityContext(
        record?.service_request_facility_id || record?.facility,
        record?.company_id || companyId
      );
      if (!facility || !record?.service_request_id) return "/switch-view";
      return (
        buildFacilityPath(`service-request-detail/${record.service_request_id}`, {
          companyId: facility.company_id,
          facilityId: facility.id,
        }) || "/switch-view"
      );
    },
    [companyId, switchToFacilityContext]
  );

  return (
    <ContractDetail
      role="portfolio"
      requestId={id}
      listPath={listPath}
      loadRequest={loadRequest}
      addNote={addNote}
      deleteNote={deleteNote}
      deleteNoteAttachment={deleteNoteAttachment}
      canAddNote={canAddNote}
      updateCcEmails={api.updateContractCcEmails}
      canEditCcEmails={canEditContract}
      closeContract={api.closeContract}
      cancel={api.cancelContract}
      reactivate={api.reactivateContract}
      award={api.awardContract}
      createWorkorder={createWorkorder}
      getWorkorderPath={getWorkorderPath}
      getTenantRequestPath={getTenantRequestPath}
    />
  );
}
